import _ from "lodash";
import { ROLES } from "@/utils/users";
import {
  REFERENCIAL_APPLICATION_NAME,
  COUNTRY_WITH_DIAGNOSTIC_REPORTS,
  COUNTRY_WITH_RD_REPORTS,
  FR_AGENCIES_WITH_NO_QUESTIONNAIRE,
  REFERENCIAL_NAME_BY_COUNTRY,
  GRPC_PARAMS,
  keepMissionsSoldInCountry,
} from "@/utils/sections";
import {
  COUNTRY_CODE,
  CALEDONIE_AGENCY_START_POSTAL_CODE,
  POLYNESIE_CASE_START_CODE,
  DOM_START_POSTAL_CODE,
} from "@/utils/localisation";

import moment from "moment";
import { ENTITY_TYPES } from "@/utils/project_enums";
import ProjectRole from "@/models/ProjectRole";
import Project from "@/models/Project";
import { BIMDATA_STATUS, SECURITY_MISSION_CODES } from "@/constants";
import { defineStore } from "pinia";
import { useUserStore } from "./user.js";
import { getCollaborationApi, getModelApi } from "@/api/bimdataClient";
import RapsoError from "@/models/RapsoError";
import { removeCacheForProjectId, retrieveCacheKeyForProjectId } from "@/setup/cache";
import {
  useBimDataApi,
  useBlueGenChatbotApi,
  useClearanceApi,
  useCurrentProjectRoleApi,
  useDictateApi,
  useJustificationApi,
  useProjectApi,
  useProjectDuplicationApi,
  useProjectMeasurementApi,
  useProjectRoleApi,
  useRoomClearanceApi,
  useSafetyReviewApi,
  useStaffingApi,
} from "@/setup/api";
import { reviveDatesDeep, serializeDatesDeep, toRapsoError } from "@/utils/utilsGrcpRest";
import { serializeProjectDates } from "@/utils/projectDates";
import { useQuestionnaireStore } from "./questionnaire.js";

const RVRAT_APIS = {
  staffings: useStaffingApi,
  clearances: useClearanceApi,
  roomClearances: useRoomClearanceApi,
  justifications: useJustificationApi,
  measurements: useProjectMeasurementApi,
  safetyReviews: useSafetyReviewApi,
};

const RVRAT_REQUEST_BODIES = {
  staffings: "staffingRequest",
  clearances: "clearanceRequest",
  roomClearances: "roomClearanceRequest",
  justifications: "justificationRequest",
  measurements: "measurementRequest",
  safetyReviews: "safetyReviewRequest",
};

const patchedBodyKey = (tableType) => {
  const body = RVRAT_REQUEST_BODIES[tableType];
  return `patched${body[0].toUpperCase()}${body.slice(1)}`;
};

export const useProjectStore = defineStore("projectStore", {
  state: () => ({
    selectedRapsotecProject: {
      case_number: null,
      address: "",
      city: "",
      webmail_email: "",
      webmail_password: "",
      missions: [],
      entity: "",
      origin_id: "",
      cloud: "",
    },
    ifcs: null,
    projectUsers: [],
    currentProjectUser: null,
    bimdataProjectToken: null,
    bimdataState: "",
    dashboardCustomParam: {
      actionPlan: 3,
      "S&D": 3,
    },
  }),
  getters: {
    projectId: (state) => state.selectedRapsotecProject.projectId,

    missionsSold: (state) =>
      keepMissionsSoldInCountry(
        state.selectedRapsotecProject.missions,
        state.selectedRapsotecProject.agencyCountry,
      ),

    unreadMail: (state) => state.selectedRapsotecProject.unreadMailNumber ?? 0,

    getSortedIfc: (state) => _.sortBy(state.ifcs, ["updated_at"]),

    rapsotecInternalUsers: (state) =>
      state.projectUsers?.filter((projectUser) => projectUser.userProfile.isFromSocotec()) ?? [],
    rapsotecExternalUsers: (state) =>
      state.projectUsers?.filter((projectUser) => !projectUser.userProfile.isFromSocotec()) ?? [],
    rapsotecProjectFormated() {
      return {
        project_name: encodeURI(this.selectedRapsotecProject.projectName),
        project_id: this.selectedRapsotecProject.projectId,
        building_permit_request_date: this.selectedRapsotecProject.buildingPermitRequestDate,
        building_permit_acquisition_date:
          this.selectedRapsotecProject.buildingPermitAcquisitionDate,
        case_postcode: this.selectedRapsotecProject.casePostcode,
        ope_start_date: this.selectedRapsotecProject.opeStartDate,
        created_at: moment(this.selectedRapsotecProject.createdAt).format("YYYY-MM-DD"),
        agency_country: this.selectedRapsotecProject.agencyCountry,
        destinations:
          this.selectedRapsotecProject.destinations?.map((destination) => ({
            code: destination,
          })) || [],
        // M.G 23/10/26 TopicViewSet.batch relit ces codes et les passe en filtre à
        // get_children_nodes_filtered : non filtrés, la création d'avis en masse en
        // pose sur des rubriques qu'aucune mission vendue n'ouvre.
        missions: this.missionsSold?.map((mission) => ({ code: mission.code })) || [],
        types: this.selectedRapsotecProject.types?.map((type) => ({
          code: type.code,
        })),
        case: {
          agency_country: this.selectedRapsotecProject.agencyCountry,
          ope_start_date: this.selectedRapsotecProject.opeStartDate,
          case_postcode: this.selectedRapsotecProject.casePostcode,
        },
        user: {
          language: useUserStore().getCurrentUserProfile.language,
        },
      };
    },
    areSomeQuestionnairesCompleted() {
      if (this.selectedRapsotecProject.questionnairesCompleted) {
        return Object.values(this.selectedRapsotecProject.questionnairesCompleted).includes(true);
      } else {
        return false;
      }
    },
    seeMoroccoFeatures: (state) =>
      state.selectedRapsotecProject.agencyCountry === COUNTRY_CODE.maroc,
    isBelgiumFeaturesAllowed: (state) =>
      state.selectedRapsotecProject.agencyCountry === COUNTRY_CODE.belgique,
    isLuxembourgFeaturesAllowed: (state) =>
      state.selectedRapsotecProject.agencyCountry === COUNTRY_CODE.luxembourg,
    isFranceFeaturesAllowed: (state) =>
      state.selectedRapsotecProject.agencyCountry === COUNTRY_CODE.france,

    isRDReportsGenerationAllowed: (state) =>
      COUNTRY_WITH_RD_REPORTS.includes(state.selectedRapsotecProject.agencyCountry),

    isDiagnosticReportsGenerationAllowed: (state) =>
      COUNTRY_WITH_DIAGNOSTIC_REPORTS.includes(state.selectedRapsotecProject.agencyCountry),
    isSecuProject: (state) => state.selectedRapsotecProject.entity === ENTITY_TYPES.SECU,
    isCTProject: (state) => state.selectedRapsotecProject.entity === ENTITY_TYPES.CT,
    isItmProject: (state) => state.selectedRapsotecProject.entity === ENTITY_TYPES.ITM,
    isItmOrLuxembourgProject() {
      return this.isItmProject || this.isLuxembourgFeaturesAllowed;
    },
    isBuildingPermitReportGenerationAllowed() {
      return (
        this.selectedRapsotecProject?.contractingAgency?.postalCode?.startsWith(
          CALEDONIE_AGENCY_START_POSTAL_CODE,
        ) &&
        this.selectedRapsotecProject?.missions
          ?.map((mission) => mission.code)
          .some((code) => SECURITY_MISSION_CODES.includes(code))
      );
    },

    isDomProject() {
      const agencyPostalCode = this.selectedRapsotecProject?.contractingAgency?.postalCode;

      if (agencyPostalCode) {
        return DOM_START_POSTAL_CODE.some((domCode) => agencyPostalCode.startsWith(domCode.code));
      }

      return false;
    },

    // C.C 16/07/26 périmètre V4 RICT/RFCT : France métro + DOM + Polynésie ; la Calédonie reste sur les OLD.
    isNewRictRfctScope() {
      const postalCode = this.selectedRapsotecProject?.contractingAgency?.postalCode;
      const isCaledonie = !!postalCode && postalCode.startsWith(CALEDONIE_AGENCY_START_POSTAL_CODE);
      const isPolynesie = !!postalCode && postalCode.startsWith(POLYNESIE_CASE_START_CODE);
      return (
        ((this.selectedRapsotecProject?.agencyCountry === COUNTRY_CODE.france ||
          this.isDomProject) &&
          !isCaledonie) ||
        isPolynesie
      );
    },

    // INFO - AG - 27/07/2022 - Display questionnaire, when it's configured, when opening a new canvas.
    // This feature is available only for french canvas ( including Reunion, Mayotte and Antille canvas)
    // We do not display questionnaire for other country (first step for BE , LU and CI)
    // We will NOT DISPLAY the questionnaire for TOM (country code FR and Nouvelle Calédonie code postal agency for the moment),
    // see utils/sections.js/ FR_AGENCIES_WITH_NO_QUESTIONNAIRE for implementation of the agency postal code related to TOM (Nouvelle Caledonie)
    isQuestionnaireToDisplay() {
      return (
        this.selectedRapsotecProject.agencyCountry === COUNTRY_CODE.france &&
        ![FR_AGENCIES_WITH_NO_QUESTIONNAIRE].some((agencyCode) =>
          this.selectedRapsotecProject.contractingAgency.postalCode?.startsWith(agencyCode),
        )
      );
    },
    getReferencialApplicationNameByProjectCountry() {
      return (
        REFERENCIAL_NAME_BY_COUNTRY[this.selectedRapsotecProject.agencyCountry] ||
        REFERENCIAL_APPLICATION_NAME.CT_RAPSOTEC_FRANCE
      );
    },
    getCanvasReferencialApplicationName() {
      // M.G 25/08/26 un projet SECU s'appuie sur le référentiel CT. RapsoSecu reste
      // consultable depuis la page Référentiels, mais plus aucun projet ne le charge.
      if (this.isItmProject) {
        return REFERENCIAL_APPLICATION_NAME.ITM_RAPSOITM_LUXEMBOURG;
      }
      return this.getReferencialApplicationNameByProjectCountry;
    },
    projectUserByUuid() {
      return (uuid) => {
        return this.projectUsers.find((user) => user.uuid === uuid);
      };
    },
    getMissionsCodes() {
      return (missions) => {
        return missions?.map((missions) => missions.code) || [];
      };
    },
    getProjectMainDestinationCode: (state) => {
      return state.selectedRapsotecProject.mainDestination;
    },
    getTypeCodes() {
      return (types) => {
        return types?.map((_type) => _type.code) || [];
      };
    },
    getCurrentProjectUserRole() {
      if (useUserStore().isDevOrSuperUser) {
        return ROLES.DEVELOPER;
      } else {
        return this.currentProjectUser.role;
      }
    },
    getProjectAccessToken() {
      return this.bimdataProjectToken ? this.bimdataProjectToken.token : null;
    },
    justificationsCount() {
      return this.selectedRapsotecProject?.justifications.length || 0;
    },
    getProjectName() {
      return this.selectedRapsotecProject.projectName;
    },
    hasReportsToAnswer() {
      if (this.selectedRapsotecProject.reportsSurvey) {
        return Object.values(this.selectedRapsotecProject.reportsSurvey).some(
          (obj) => !obj.answered,
        );
      } else {
        return false;
      }
    },
    getReportUuidsToAnswer() {
      const reports_survey = this.selectedRapsotecProject.reportsSurvey;
      if (reports_survey) {
        return Object.keys(reports_survey).filter((k) => !reports_survey[k]["answered"]);
      } else {
        return [];
      }
    },
    getProjectParams() {
      // M.G 02/09/26 un projet SECU suit désormais le référentiel CT de son pays, comme
      // getCanvasReferencialApplicationName juste au-dessus : il n'a plus de paramètres
      // propres et retombe volontairement sur ceux du pays.
      if (this.isItmProject) {
        return GRPC_PARAMS.NATIONAL_CURRENT_RAPSOITM_PARAMS;
      }
      if (this.seeMoroccoFeatures) {
        return GRPC_PARAMS.MOROCCO_CURRENT_PARAMS;
      } else {
        return GRPC_PARAMS.NATIONAL_CURRENT_PARAMS;
      }
    },
    hasNoProjectManager() {
      return !this.selectedRapsotecProject.projectManager;
    },
  },
  actions: {
    async getRapsotecProject(projectId, force = false) {
      if (this.selectedRapsotecProject.projectId !== projectId || force) {
        const project = await useProjectApi().retrieve({ projectId });
        this.setRapsotecProject(serializeProjectDates(project));

        const application_name = this.getCanvasReferencialApplicationName;
        const local_storage_key = `lastSectionsUpdated.${application_name}`;
        let lastSectionsUpdated = localStorage.getItem(local_storage_key);

        let getProjectLastSectionUpdated = await retrieveCacheKeyForProjectId(projectId);

        if (!getProjectLastSectionUpdated || !window.caches) {
          return;
        }

        getProjectLastSectionUpdated = new URL(getProjectLastSectionUpdated.url);

        getProjectLastSectionUpdated = getProjectLastSectionUpdated.searchParams.get("last_update");

        if (getProjectLastSectionUpdated !== lastSectionsUpdated) {
          await removeCacheForProjectId(projectId);
        }
      }
    },
    async fetchProjectEntity(projectId) {
      const { results } = await useProjectApi().list({ projectId, pageSize: 1 });
      return results?.[0] ?? null;
    },
    async fetchProjectUsers() {
      const projectUsers = await useProjectRoleApi().list({
        projectId: this.selectedRapsotecProject.projectId,
      });
      this.projectUsers = serializeDatesDeep(projectUsers).map(
        (projectUser) => new ProjectRole(projectUser),
      );
    },
    async fetchProjectIfc() {
      try {
        const modelApi = await getModelApi();
        const cloudId = this.selectedRapsotecProject.cloud;
        const originId = this.selectedRapsotecProject.originId;
        const ifcs = await modelApi.getModels(cloudId, originId, {
          type: ["IFC"],
        });
        if (!ifcs) {
          return;
        }
        this.ifcs = ifcs;

        let pendingStatus = ["P", "I"];
        let hasPendingIfc = ifcs.some((ifc) => pendingStatus.includes(ifc.status));

        if (hasPendingIfc) {
          const idInterval = setInterval(async () => {
            const ifcsPending = await modelApi.getModels(cloudId, originId, {
              type: ["IFC"],
              status: pendingStatus,
            });
            if (ifcsPending.length === 0) {
              const ifcs = await modelApi.getModels(cloudId, originId, {
                type: ["IFC"],
              });
              this.ifcs = ifcs;
              clearInterval(idInterval);
            }
          }, 5000);
        }
        return ifcs;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchCurrentProjectUser({ projectId }) {
      const currentProjectRole = await useCurrentProjectRoleApi().retrieve({ projectId });
      // fetchCurrentProjectUser only get information about the use rin the project
      // if we want to use information about user profile we need to get them form the user store
      this.currentProjectUser = new ProjectRole({
        ...serializeDatesDeep(currentProjectRole),
        userProfile: useUserStore().getCurrentUserProfile,
      });
    },

    async createProjectUser(projectRoleInput) {
      try {
        const projectUser = await useProjectRoleApi().create({
          projectId: this.selectedRapsotecProject.projectId,
          projectRoleInputRequest: projectRoleInput,
        });
        this.projectUsers.unshift(new ProjectRole(serializeDatesDeep(projectUser)));
      } catch (err) {
        throw await toRapsoError(err);
      }
    },

    async updateProjectUser({ projectUserUuid, projectRoleWrite }) {
      const projectUser = await useProjectRoleApi().partialUpdate({
        projectId: this.selectedRapsotecProject.projectId,
        uuid: projectUserUuid,
        patchedProjectRoleWriteRequest: projectRoleWrite,
      });
      this.projectUsers.forEach((item, index) => {
        if (item.uuid === projectUser.uuid) {
          this.projectUsers.splice(index, 1, new ProjectRole(serializeDatesDeep(projectUser)));
        }
      });
    },

    async updateSelfProjectUser(projectRoleWrite) {
      const currentProjectRole = await useCurrentProjectRoleApi().partialUpdate({
        projectId: this.selectedRapsotecProject.projectId,
        patchedProjectRoleWriteRequest: projectRoleWrite,
      });
      this.currentProjectUser.setData({
        ...serializeDatesDeep(currentProjectRole),
        userProfile: this.currentProjectUser.userProfile,
      });
    },

    async deleteProjectUser(projectUserUuid) {
      await useProjectRoleApi().destroy({
        projectId: this.selectedRapsotecProject.projectId,
        uuid: projectUserUuid,
      });
    },
    async getDocumentUrl({ idCloud, bimProjectId, idDocument }) {
      const collaborationApi = await getCollaborationApi();
      const { file } = await collaborationApi.getDocument(idCloud, idDocument, bimProjectId);
      return file;
    },
    async getDocumentDetails({ idCloud, bimProjectId, idDocument }) {
      const collaborationApi = await getCollaborationApi();
      const details = await collaborationApi.getDocument(idCloud, idDocument, bimProjectId);
      return details;
    },
    async openDocument({ idCloud, bimProjectId, idDocument }) {
      const collaborationApi = await getCollaborationApi();
      const { file } = await collaborationApi.getDocument(idCloud, idDocument, bimProjectId);
      window.open(file, "_blank");
    },
    async updateRapsotecProject({ projectNewData }) {
      try {
        const project = await useProjectApi().partialUpdate({
          projectId: this.selectedRapsotecProject.projectId,
          patchedProjectUpdateRequest: reviveDatesDeep(projectNewData),
        });
        this.setRapsotecProject(serializeProjectDates(project));
        // INFO - NL - 09/12/2021 - Suppose that mission codes have changed, need to reload questionnaires
        useQuestionnaireStore().questionnairesFetched = false;
      } catch (err) {
        throw await toRapsoError(err);
      }
    },
    async getResumeDuplicateProject(requestDatas) {
      try {
        return await useProjectDuplicationApi().getResume({
          projectId: this.selectedRapsotecProject.projectId,
          projectDuplicationRequestRequest: requestDatas,
        });
      } catch (err) {
        throw await toRapsoError(err);
      }
    },
    async applyDuplicateProject(requestDatas) {
      try {
        return await useProjectDuplicationApi().create({
          projectId: this.selectedRapsotecProject.projectId,
          projectDuplicationRequestRequest: requestDatas,
        });
      } catch (err) {
        throw await toRapsoError(err);
      }
    },
    async fetchProjectExtra() {
      const data = await useProjectApi().retrieveExtra({
        projectId: this.selectedRapsotecProject.projectId,
      });
      this.selectedRapsotecProject = new Project({
        ...this.selectedRapsotecProject,
        ...data,
      });
      this.selectedRapsotecProject.extraDataFetched = true;
    },
    async fetchProjectAccessToken() {
      try {
        const { token } = await useBimDataApi().getProjectAccessToken({
          projectId: this.selectedRapsotecProject.projectId,
        });
        return token;
      } catch (err) {
        this.bimdataState = BIMDATA_STATUS.ERROR;
        throw await toRapsoError(err);
      }
    },
    async fetchBlueGenToken() {
      try {
        const { accessToken } =
          await useBlueGenChatbotApi().aiServicesBluegenChatbotTokenRetrieve();
        return accessToken;
      } catch (err) {
        throw await toRapsoError(err);
      }
    },
    async updateSortOrderRVRATTables({ tableType, safetyReviewUuid, uuids }) {
      const projectId = this.selectedRapsotecProject.projectId;
      if (safetyReviewUuid) {
        return useDictateApi().updateSortOrder({
          projectId,
          safetyReviewUuid,
          requestBody: uuids,
        });
      }
      await RVRAT_APIS[tableType]().updateSortOrder({
        projectId,
        requestBody: uuids,
      });
    },
    async createRVRATRow(tableType, row) {
      const created = await RVRAT_APIS[tableType]().create({
        projectId: this.selectedRapsotecProject.projectId,
        [RVRAT_REQUEST_BODIES[tableType]]: reviveDatesDeep(row),
      });
      return serializeDatesDeep(created);
    },
    async updateRVRATRow(tableType, uuid, row) {
      const updated = await RVRAT_APIS[tableType]().partialUpdate({
        projectId: this.selectedRapsotecProject.projectId,
        uuid,
        [patchedBodyKey(tableType)]: reviveDatesDeep(row),
      });
      return serializeDatesDeep(updated);
    },
    async deleteRVRATRow(tableType, uuid) {
      await RVRAT_APIS[tableType]().destroy({
        projectId: this.selectedRapsotecProject.projectId,
        uuid,
      });
    },
    async createDictate(safetyReviewUuid, dictate) {
      const created = await useDictateApi().create({
        projectId: this.selectedRapsotecProject.projectId,
        safetyReviewUuid,
        dictateRequest: reviveDatesDeep(dictate),
      });
      return serializeDatesDeep(created);
    },
    async updateDictate(safetyReviewUuid, uuid, dictate) {
      const updated = await useDictateApi().partialUpdate({
        projectId: this.selectedRapsotecProject.projectId,
        safetyReviewUuid,
        uuid,
        patchedDictateRequest: reviveDatesDeep(dictate),
      });
      return serializeDatesDeep(updated);
    },
    async deleteDictate(safetyReviewUuid, uuid) {
      await useDictateApi().destroy({
        projectId: this.selectedRapsotecProject.projectId,
        safetyReviewUuid,
        uuid,
      });
    },
    async fetchRVRATData() {
      const rvratData = await useProjectApi().retrieveRvratDatas({
        projectId: this.selectedRapsotecProject.projectId,
      });
      this.patchRapsotecProject(serializeProjectDates(rvratData));
    },
    async fetchJustification(uuid) {
      const justification = await useJustificationApi().retrieve({
        projectId: this.selectedRapsotecProject.projectId,
        uuid,
      });
      return serializeDatesDeep(justification);
    },
    async updateJustification(uuid, justificationData) {
      const justification = await useJustificationApi().partialUpdate({
        projectId: this.selectedRapsotecProject.projectId,
        uuid,
        patchedJustificationRequest: justificationData,
      });
      return serializeDatesDeep(justification);
    },
    async patchProjectData(dataToUpdate) {
      const project = await useProjectApi().partialUpdate({
        projectId: this.selectedRapsotecProject.projectId,
        patchedProjectUpdateRequest: reviveDatesDeep(dataToUpdate),
      });
      this.patchRapsotecProject(serializeProjectDates(project));
    },
    resetProject() {
      this.selectedRapsotecProject = {
        caseNumber: null,
        address: "",
        city: "",
        webmailEmail: "",
        webmailPassword: "",
        missions: [],
        entity: "",
      };
      this.ifcs = null;
      this.projectUsers = [];
      this.currentProjectUser = null;
      this.bimdataProjectToken = null;
      this.bimdataState = "";
    },
    setRapsotecProject(selectedRapsotecProject) {
      this.selectedRapsotecProject = new Project(selectedRapsotecProject);
    },
    patchRapsotecProject(data) {
      this.selectedRapsotecProject = new Project({
        ...this.selectedRapsotecProject,
        ...data,
      });
    },
    setTopicPaneSize(value) {
      this.topicPaneSize = value;
    },

    setBimdataProjectToken(token) {
      this.bimdataProjectToken = token;
    },
    // INFO - AG - 01/06/2022 - Waiting for a response from BimData API
    setBimdataIsLoading() {
      this.bimdataState = BIMDATA_STATUS.LOADING;
    },
    // INFO - AG - 01/06/2022 - BimData token is loaded and use can access every where Bimdata token is necessary
    setBimdataIsLoaded() {
      this.bimdataState = BIMDATA_STATUS.SUCCESS;
    },
  },
});
